import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { getStationRelevance, getDeviceFuns } from '@/api/powerStation'
import {
  AccessDevice,
  Device,
  Gateway,
  ModFun,
  StationMqttServer,
  Station,
  StationVirtualLabel,
  StationVirtualPoint,
  FunGroup,
  Fun,
} from '@/type/stationType'
import MqttServer from '@/utils/mqttUtil'
import { getGatewayStatus } from '@/utils/getStatus'
import useGlobalConfigStore from './globalConfig'
import appConfig from '@/config'

interface StationState {
  station: Station
  access_device: AccessDevice[]
  device: Device[]
  device_type: { code_id: string; code_name: string }[]
  gateway: Gateway[]
  mod_funs: ModFun[]
  mqtt_server: StationMqttServer[]
  mqtt_serve: MqttServer[]
  station_type: { code_id: string; code_name: string }[]
  station_status_type: { code_id: string; code_name: string }[]
  station_virtual_label: StationVirtualLabel[]
  station_virtual_point: StationVirtualPoint[]
}

interface StationAction {
  stationInit: (station_id: number) => Promise<void>
}

const station = {
  LFDI: '',
  NMI: '',
  NMI_reg: 0,
  address: '',
  agent: '',
  area: '',
  bracket: '',
  capacity_ratio: '',
  collect_time: '',
  contract_no: '',
  create_name: '',
  create_time: '',
  create_user: '',
  currency: '',
  device_auth: 0,
  device_control: 0,
  device_setting: 0,
  elevation: '',
  file_id: '',
  first_user: '',
  icon: null,
  id: 0,
  installed_capacity: '',
  is_collect: 0,
  latitude: '',
  link_time: '',
  longitude: '',
  postal_code: '',
  rated_capacity: '',
  remark: '',
  run_remark: '',
  run_status: 1,
  service_custom: '',
  station_auth: 0,
  station_image: [] as string[],
  station_name: '',
  station_type: '',
  station_type_name: '',
  status: '',
  total_radiation: '',
  update_time: '',
  update_user: '',
  weather: '',
  status_name: '',
}

const useOneStationStore = create<StationState & StationAction>()(
  immer((set) => ({
    station,
    access_device: [],
    device: [],
    device_type: [],
    gateway: [],
    mod_funs: [],
    mqtt_server: [],
    mqtt_serve: [],
    station_type: [],
    station_status_type: [],
    station_virtual_label: [],
    station_virtual_point: [],

    stationInit: async (station_id: number) => {
      let res: any, res2: any
      useGlobalConfigStore.getState().setLoading(true)
      try {
        res = await getStationRelevance({ station_id })
      } catch (e) {
        console.log(e)
        useGlobalConfigStore.getState().setLoading(false)
        throw new Error('Get Station error')
      }
      const data: any = res.data
      if (data.station.length > 0) {
        data.station[0].station_image = JSON.parse(data.station[0].station_image) as string[]
        data.station[0].status_name = data.station_status_type.find(
          (item: any) => item.code_id === data.station[0].status
        ).code_name
        data.station_virtual_point.map((point: any) => {
          const target = data.station_virtual_label.find((label: any) => label.id === point.label_id)
          if (target) {
            if (target.points) {
              target.points.push(point)
            } else {
              target.points = [point]
            }
          }
        })

        //  mqtt
        let mqttServe: MqttServer[] = [],
          promiseArr: Promise<any>[] = []
        data.mqtt_server.map((server: any) => {
          promiseArr.push(
            new Promise((resolve, reject) => {
              mqttServe[server.mqtt_id] = new MqttServer({
                mqtt_host: server.mqtt_host_ip,
                mqtt_ws_port: Number(server.mqtt_host_ws_port),
                mqtt_user: server.mqtt_user,
                mqtt_pwd: server.mqtt_pwd,
              })
              mqttServe[server.mqtt_id].init(resolve, reject)
            })
          )
        })
        try {
          res2 = await Promise.all(promiseArr)
        } catch (e) {
          console.log(e)
          useGlobalConfigStore.getState().setLoading(false)
          throw new Error('Mqtt Init error')
        }

        //  gateway
        for (let oneGateway of data.gateway) {
          let gatewayStatus: any
          try {
            gatewayStatus = await getGatewayStatus(mqttServe[oneGateway.mqtt_server_id], oneGateway.uuid)
            if (!gatewayStatus.status) {
              useGlobalConfigStore.getState().setLoading(false)
              throw new Error('Get Gateway Status Error')
            }
          } catch (e) {
            console.log(e)
            useGlobalConfigStore.getState().setLoading(false)
            throw new Error('Get Gateway Status Error')
          }
        }

        // device
        for (let oneDevice of data.device as Device[]) {
          const targetAccessDevice = data.access_device.find(
            (item: AccessDevice) => item.serial_no === oneDevice.iot_serial_no
          )
          if (targetAccessDevice) {
            oneDevice.device_code = targetAccessDevice.device_code
            oneDevice.parent_id = targetAccessDevice.parent_id
            oneDevice.device_id = targetAccessDevice.device_id
            oneDevice.mqtt_server_id = data.gateway.find(
              (item: Gateway) => item.uuid === oneDevice.iot_gateway_uuid
            )?.mqtt_server_id
            oneDevice.device_pic =
              appConfig.staticFile +
              'static/images/product/' +
              appConfig.prefix.toLowerCase() +
              '/' +
              oneDevice.device_pic
            if (oneDevice.device_type === 'C002') {
              try {
                if (targetAccessDevice.mod_id === 15) {
                  res = await getDeviceFuns({
                    gateway_uuid: oneDevice.iot_gateway_uuid,
                    tempid: 13,
                    modid: 15,
                  })
                } else if (targetAccessDevice.mod_id === 18) {
                  res = await getDeviceFuns({
                    gateway_uuid: oneDevice.iot_gateway_uuid,
                    tempid: 26,
                    modid: 18,
                  })
                }
                if (res.code === 1001) {
                  res.data.funs.forEach((fun: any) => {
                    fun.type_detail = JSON.parse(fun.type_detail)
                  })
                  res.data.groups.forEach((item: FunGroup) => {
                    item.funs = res.data.funs.filter((fun: Fun) => fun.group_id === item.group_id)
                  })
                  oneDevice.fun_group = res.data.groups
                }
              } catch (e) {
                console.log(e)
                useGlobalConfigStore.getState().setLoading(false)
                throw new Error('Get Device Fun Error')
              }
            }
          }
        }

        set((state) => {
          state.station = data.station[0]
          state.access_device = data.access_device
          state.device = data.device
          state.device_type = data.device_type
          state.gateway = data.gateway
          state.mod_funs = data.mod_funs
          state.mqtt_server = data.mqtt_server
          state.mqtt_serve = mqttServe
          state.station_type = data.station_type
          state.station_status_type = data.station_status_type
          state.station_virtual_label = data.station_virtual_label
          state.station_virtual_point = data.station_virtual_point
        })
        useGlobalConfigStore.getState().setLoading(false)
        useGlobalConfigStore.getState().setInitFinished(true)
      }
    },
  }))
)

export default useOneStationStore
